/** Narrow broker badge and account short-name columns.
 * Broker colors are ready for Robinhood, Tastytrade, Webull, and Fidelity.
 * Keep defaultAccountShortName / normalizeAccountShortName in sync with server/ledgerMarks.mjs.
 */

import { loadLedgerCatalog, type LedgerAccount } from "./ownLedgerClient";

const BROKER_WORDS = new Set([
  "robinhood",
  "webull",
  "fidelity",
  "tastytrade",
  "tastytrades",
  "tasty",
  "trades",
]);

const FILLER_WORDS = new Set(["account", "accounts", "brokerage", "the", "of", "and", "my"]);

export interface BrokerBadge {
  code: string;
  color: string;
  title: string;
}

export interface LedgerDisplayMark {
  brokerCode: string;
  brokerColor: string;
  brokerTitle: string;
  accountShort: string;
  accountTitle: string;
}

interface HoldingLot {
  accountId: string;
  qty: number;
}

interface LedgerMarkIndex {
  accounts: Map<string, LedgerAccount>;
  holdings: Map<string, HoldingLot[]>;
  activeAccountIds: string[];
}

const BROKER_BADGES: Record<string, BrokerBadge> = {
  robinhood: { code: "RH", color: "#00c805", title: "Robinhood" },
  tastytrade: { code: "TT", color: "#e85d04", title: "Tastytrade" },
  webull: { code: "WB", color: "#0b4dff", title: "Webull" },
  fidelity: { code: "FD", color: "#3d8b37", title: "Fidelity" },
  manual: { code: "MN", color: "#8b93a7", title: "Manual" },
  paper: { code: "PA", color: "#8b5cf6", title: "Paper" },
};

const listeners = new Set<() => void>();
let index: LedgerMarkIndex | null = null;
let loadedAt = 0;
let inflight: Promise<LedgerMarkIndex | null> | null = null;
let paperHoldingsMode = false;

export function setLedgerHoldingsPaperMode(on: boolean): void {
  paperHoldingsMode = on === true;
}

function lotIsPaper(accountId: string): boolean {
  const id = String(accountId ?? "").trim().toLowerCase();
  return (
    id.startsWith("webull:paper:") ||
    id.startsWith("paper_agent") ||
    id.includes(":paper:") ||
    id.startsWith("paper:")
  );
}

export function defaultAccountShortName(label: string | null | undefined): string {
  const raw = String(label ?? "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  const letters = raw.replace(/[^a-zA-Z]/g, "");
  if (digits.length >= 4 && letters.length < 3) return digits.slice(-4);
  const tokens = raw
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const meaningful = tokens.filter(
    (token) => !BROKER_WORDS.has(token.toLowerCase()) && !FILLER_WORDS.has(token.toLowerCase()),
  );
  const use = meaningful.length ? meaningful : tokens;
  if (!use.length) return "";
  if (use.length === 1) return use[0].slice(0, 3).toUpperCase();
  return use
    .map((token) => token[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();
}

export function normalizeAccountShortName(value: string | null | undefined): string {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function blankBroker(brokerId: string | null | undefined, brokerName?: string | null): boolean {
  const id = String(brokerId ?? "").trim().toLowerCase();
  const name = String(brokerName ?? "").trim().toLowerCase();
  return !id || id === "undefined" || id === "unassigned" || name === "undefined";
}

export function brokerBadgeFor(
  brokerId: string | null | undefined,
  brokerName?: string | null,
): BrokerBadge | null {
  if (blankBroker(brokerId, brokerName)) return null;
  const id = String(brokerId ?? "").trim().toLowerCase();
  const known = BROKER_BADGES[id];
  if (known) return known;
  const source = String(brokerName || brokerId || "").replace(/[^a-zA-Z0-9]/g, "");
  const code = source.slice(0, 2).toUpperCase();
  if (!code) return null;
  const title = String(brokerName || brokerId || code).trim();
  return { code, color: "#a78bfa", title };
}

export function subscribeLedgerMarks(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyLedgerMarks(): void {
  for (const listener of listeners) listener();
}

function buildIndex(payload: {
  accounts?: LedgerAccount[];
  holdings?: Array<{ ticker?: string; account_id?: string; qty?: number }>;
  active_account_ids?: string[];
}): LedgerMarkIndex {
  const accounts = new Map<string, LedgerAccount>();
  for (const account of payload.accounts ?? []) {
    if (account?.account_id) accounts.set(account.account_id, account);
  }
  const holdings = new Map<string, HoldingLot[]>();
  for (const row of payload.holdings ?? []) {
    const ticker = String(row.ticker ?? "").trim().toUpperCase();
    const accountId = String(row.account_id ?? "").trim();
    const qty = Number(row.qty);
    if (!ticker || !accountId || !(qty > 0)) continue;
    const lots = holdings.get(ticker) ?? [];
    lots.push({ accountId, qty });
    holdings.set(ticker, lots);
  }
  const activeAccountIds = [...new Set((payload.active_account_ids ?? []).map((id) => String(id).trim()).filter(Boolean))];
  return { accounts, holdings, activeAccountIds };
}

export function peekLedgerMarkIndex(): LedgerMarkIndex | null {
  return index;
}

export function invalidateLedgerMarkIndex(): void {
  index = null;
  loadedAt = 0;
}

export async function ensureLedgerMarkIndex(force = false): Promise<LedgerMarkIndex | null> {
  if (!force && index && Date.now() - loadedAt < 60_000) return index;
  if (inflight && !force) return inflight;
  const run = (async () => {
    try {
      const catalog = await loadLedgerCatalog(force);
      index = buildIndex(catalog);
      loadedAt = Date.now();
      notifyLedgerMarks();
      return index;
    } catch {
      return index;
    } finally {
      inflight = null;
    }
  })();
  inflight = run;
  return run;
}

function accountShortFromCatalog(account: LedgerAccount | undefined): string {
  const stored = normalizeAccountShortName(account?.short_name);
  if (stored) return stored;
  return defaultAccountShortName(account?.label || account?.name || "");
}

/** Active ledger accounts, with the broker badge and short name used in tables. */
export function listLedgerAccountMarks(): Array<{ id: string; mark: LedgerDisplayMark }> {
  if (!index) return [];
  const ids = index.activeAccountIds.length
    ? index.activeAccountIds
    : [...index.accounts.keys()];
  const out: Array<{ id: string; mark: LedgerDisplayMark }> = [];
  for (const id of ids) {
    const mark = markForAccountId(id);
    if (mark) out.push({ id, mark });
  }
  return out;
}

export function markForAccountId(accountId: string | null | undefined): LedgerDisplayMark | null {
  const id = String(accountId ?? "").trim();
  if (!id || !index) return null;
  const account = index.accounts.get(id);
  if (!account) return null;
  const badge = brokerBadgeFor(account.broker_id, account.broker_name);
  const accountShort = accountShortFromCatalog(account);
  if (!badge && !accountShort) return null;
  return {
    brokerCode: badge?.code ?? "",
    brokerColor: badge?.color ?? "",
    brokerTitle: badge?.title ?? "",
    accountShort,
    accountTitle: String(account.label || account.name || accountShort).trim(),
  };
}

export interface LedgerRowMarkSource {
  account_id?: string | null;
  account_short_name?: string | null;
  account_label?: string | null;
  account_name?: string | null;
  broker_id?: string | null;
  broker_name?: string | null;
}

/** Transaction row: catalog short name when loaded, otherwise the value stored on the row. */
export function ledgerRowMark(row: LedgerRowMarkSource | null | undefined): LedgerDisplayMark | null {
  if (!row) return null;
  const live = markForAccountId(row.account_id);
  const badge = brokerBadgeFor(row.broker_id, row.broker_name);
  const storedShort = normalizeAccountShortName(row.account_short_name);
  const fallbackShort = storedShort || defaultAccountShortName(row.account_label || row.account_name);
  const accountTitle = String(row.account_label || row.account_name || fallbackShort).trim();
  if (live) {
    return {
      ...live,
      brokerCode: live.brokerCode || badge?.code || "",
      brokerColor: live.brokerColor || badge?.color || "",
      brokerTitle: live.brokerTitle || badge?.title || "",
      accountTitle: live.accountTitle || accountTitle,
    };
  }
  if (!badge && !fallbackShort) return null;
  return {
    brokerCode: badge?.code ?? "",
    brokerColor: badge?.color ?? "",
    brokerTitle: badge?.title ?? "",
    accountShort: fallbackShort,
    accountTitle,
  };
}

/** Put/call recommendation: the account that holds the shares, or the only active account. */
export function recommendationLedgerMark(ticker: string | null | undefined): LedgerDisplayMark | null {
  if (!index) return null;
  const key = String(ticker ?? "").trim().toUpperCase();
  const lots = [...(index.holdings.get(key) ?? [])]
    .filter((lot) => lotIsPaper(lot.accountId) === paperHoldingsMode)
    .sort((a, b) => b.qty - a.qty);
  let accountId = "";
  let extra = "";
  if (lots.length) {
    accountId = lots[0].accountId;
    if (lots.length > 1) extra = ` · also in ${lots.length - 1} other account${lots.length === 2 ? "" : "s"}`;
  } else if (index.activeAccountIds.length === 1) {
    accountId = index.activeAccountIds[0];
  }
  const mark = markForAccountId(accountId);
  if (!mark) return null;
  return extra ? { ...mark, accountTitle: `${mark.accountTitle}${extra}` } : mark;
}

export function brokerMarkInnerHtml(mark: LedgerDisplayMark | null): string {
  if (!mark?.brokerCode) {
    return `<span class="sfe-broker-mark sfe-mark-blank" aria-hidden="true">—</span>`;
  }
  const title = mark.brokerTitle ? ` title="${esc(mark.brokerTitle)}"` : "";
  return `<span class="sfe-broker-mark" style="color:${esc(mark.brokerColor)}"${title}>${esc(mark.brokerCode)}</span>`;
}

export function accountShortInnerHtml(mark: LedgerDisplayMark | null): string {
  if (!mark?.accountShort) {
    return `<span class="sfe-acct-short sfe-mark-blank" aria-hidden="true">—</span>`;
  }
  const title = mark.accountTitle ? ` title="${esc(mark.accountTitle)}"` : "";
  return `<span class="sfe-acct-short"${title}>${esc(mark.accountShort)}</span>`;
}

export function brokerMarkCellHtml(mark: LedgerDisplayMark | null): string {
  return `<td class="sfe-mark-col" data-col="broker_mark">${brokerMarkInnerHtml(mark)}</td>`;
}

export function accountShortCellHtml(mark: LedgerDisplayMark | null): string {
  return `<td class="sfe-mark-col sfe-acct-col" data-col="account_short">${accountShortInnerHtml(mark)}</td>`;
}
